import React, { useEffect } from "react";
import { Button, Modal, ModalBody, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import SimpleBar from "simplebar-react";
import "../../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import { parseLocalDate } from "helpers/constant";

/* =========================================================
   Filter Value Interface
========================================================= */

export interface StockLedgerFilterValues {
  transactiontype?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  ipcategory?: string | null;
}

/* =========================================================
   Props
========================================================= */

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onApply: (filters: StockLedgerFilterValues) => void;
  onClear: () => void;
  existingFilters?: StockLedgerFilterValues;
}

/* =========================================================
   Static Options
========================================================= */

const TRANSACTION_TYPE_OPTIONS = [
  { label: "IN", value: "IN" },
  { label: "OUT", value: "OUT" },
  { label: "RETURN", value: "RETURN" },
  { label: "TRANSFER", value: "TRANSFER" },
  { label: "DISPOSAL", value: "DISPOSE" },
  { label: "RETENTION", value: "RETENTION" },
];

const IP_CATEGORY_OPTIONS = [
  { label: "Test Product", value: "Test Product" },
  { label: "Reference Product", value: "Reference Product" },
];

/* =========================================================
   Component
========================================================= */

const StockLedgerFilterModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onApply,
  onClear,
  existingFilters,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<StockLedgerFilterValues>({
    defaultValues: existingFilters ?? {},
  });

  /* ---------------- DATE FORMATTER ---------------- */
  const handleDateChange = (
    dates: Date[],
    onChange: (val: string | null) => void,
  ) => {
    if (dates && dates[0]) {
      const d = dates[0];
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      onChange(`${yyyy}-${mm}-${dd}`);
    } else {
      onChange(null);
    }
  };

  /* ---------------- REACT SELECT STYLES ---------------- */
  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#f3f3f3",
      borderColor: state.isFocused ? "#405189" : "transparent",
      borderRadius: "4px",
      fontSize: "12px",
      minHeight: "27px",
      height: "auto",
      boxShadow: "none",
      "&:hover": { borderColor: "#405189" },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 6px",
      fontSize: "12px",
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#495057",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#adb5bd",
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: "25px",
    }),
    option: (base: any) => ({
      ...base,
      fontSize: "12px",
      padding: "6px 10px",
      minHeight: "25px",
      display: "flex",
      alignItems: "center",
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  /* ---------------- APPLY FILTER ---------------- */

  const onSubmit = (values: StockLedgerFilterValues) => {
    const normalizedFilters: StockLedgerFilterValues = {
      transactiontype: values.transactiontype || null,
      fromDate: values.fromDate || null,
      toDate: values.toDate || null,
      ipcategory: values.ipcategory || null,
    };
    onApply(normalizedFilters);
    toggle();
  };

  /* ---------------- CLEAR FILTER ---------------- */

  const handleClear = () => {
    onClear();
    toggle();
    reset({
      transactiontype: null,
      fromDate: null,
      toDate: null,
      ipcategory: null,
    });
  };

  useEffect(() => {
    if (existingFilters && Object.keys(existingFilters).length > 0) {
      reset(existingFilters);
    } else {
      reset({
        transactiontype: null,
        fromDate: null,
        toDate: null,
        ipcategory: null,
      });
    }
  }, [existingFilters, reset]);

  /* ---------------- RENDER ---------------- */

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      backdrop="static"
      className="volunteer-filter-modal common_modal"
    >
      <div style={{ fontFamily: '"hkgrotesk", "Poppins", sans-serif' }}>
        <div className="modal-header">
          <h5 className="modal-title-custom">Filter Stock Ledger Report</h5>
          <p className="modal-subtitle">
            Apply specific criteria to narrow down your selection
          </p>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={toggle}
          ></button>
        </div>

        <ModalBody className="p-0">
          <SimpleBar className="p-4" style={{ maxHeight: "400px" }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="filter-section">
                <h6 className="section-title">REPORT FILTERS</h6>
                <div className="row">
                  
                  {/* Transaction Type */}
                  <div className="col-md-6 mb-3">
                    <div className="form-group mb-0">
                      <label>TRANSACTION TYPE</label>
                      <Controller
                        name="transactiontype"
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={TRANSACTION_TYPE_OPTIONS}
                            styles={customSelectStyles}
                            placeholder="Select Transaction Type"
                            isClearable
                            menuPortalTarget={document.body}
                            value={TRANSACTION_TYPE_OPTIONS.find((opt: any) => opt.value === field.value) || null}
                            onChange={(val: any) => field.onChange(val ? val.value : null)}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* IP CATEGORY */}
                  <div className="col-md-6 mb-3">
                    <div className="form-group mb-0">
                      <label>IP CATEGORY</label>
                      <Controller
                        name="ipcategory"
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={IP_CATEGORY_OPTIONS}
                            styles={customSelectStyles}
                            placeholder="Select IP Category"
                            isClearable
                            menuPortalTarget={document.body}
                            value={IP_CATEGORY_OPTIONS.find((opt: any) => opt.value === field.value) || null}
                            onChange={(val: any) => field.onChange(val ? val.value : null)}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="col-md-6 mb-3">
                    <div className="form-group mb-0">
                      <label>TRANSACTION DATE RANGE</label>
                      <div className="range-inputs">
                        <Controller
                          name="fromDate"
                          control={control}
                          render={({ field }) => (
                            <Flatpickr
                              className="form-control"
                              options={{ dateFormat: "d/m/Y" }}
                              placeholder="dd/mm/yyyy"
                              value={field.value ? (parseLocalDate(field.value) ?? undefined) : undefined}
                              onChange={(dates: Date[]) => handleDateChange(dates, field.onChange)}
                            />
                          )}
                        />
                        <span className="range-separator">to</span>
                        <Controller
                          name="toDate"
                          control={control}
                          render={({ field }) => (
                            <Flatpickr
                              className="form-control"
                              options={{ dateFormat: "d/m/Y" }}
                              placeholder="dd/mm/yyyy"
                              value={field.value ? (parseLocalDate(field.value) ?? undefined) : undefined}
                              onChange={(dates: Date[]) => handleDateChange(dates, field.onChange)}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </form>
          </SimpleBar>
        </ModalBody>

        <div className="modal-footer-custom">
          <button type="button" className="btn-clear" onClick={handleClear}>
            <i className="ri-refresh-line"></i> Clear Filters
          </button>

          <div className="d-flex gap-2">
            <Button color="secondary" outline onClick={toggle}>
              Cancel
            </Button>
            <Button disabled={isSubmitting} onClick={handleSubmit(onSubmit)} outline color="primary">
              {isSubmitting && <Spinner size="sm" className="me-2" />}
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default StockLedgerFilterModal;
